import React, { useState } from "react";
import { Moon, Mail, Lock, Loader2, Calendar, MessageSquare, Wallet, User, LogOut, FileText, Clock } from "lucide-react";
import { toast } from "sonner";

import StarField from "../components/StarField";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { MOCK_USER, MOCK_BOOKINGS, MOCK_MESSAGES } from "../mock/mockData";

const PortalPage = () => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [tab, setTab] = useState("overview");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    await new Promise((r) => setTimeout(r, 700));
    setLoading(false);
    // Demo: any credentials work for preview
    setLoggedIn(true);
    toast.success(`Welcome back, ${MOCK_USER.name.split(" ")[0]}!`);
  };

  const handleLogout = () => {
    setLoggedIn(false);
    setCreds({ email: "", password: "" });
    toast("You've been logged out.");
  };

  if (!loggedIn) {
    return (
      <div className="relative min-h-[80vh] flex items-center" data-testid="portal-login">
        <StarField count={60} />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,165,116,0.1),transparent_60%)]" />
        <div className="container-lux relative max-w-md">
          <div className="lux-card rounded-[2rem] p-10 md:p-12">
            <div className="text-center mb-8">
              <Moon className="w-10 h-10 text-gold mx-auto mb-5 moon-glow" />
              <div className="divider-gold mb-4 mx-auto">Client Portal</div>
              <h1 className="font-serif text-4xl leading-tight">Welcome back.</h1>
              <p className="text-white/60 text-sm mt-3">Sign in to access your plan, bookings, and team.</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase text-gold mb-2 block">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    required
                    type="email"
                    value={creds.email}
                    onChange={(e) => setCreds({ ...creds, email: e.target.value })}
                    placeholder="you@example.com"
                    className="bg-deep-surface border-white/10 text-white input-lux pl-11"
                    data-testid="portal-email"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase text-gold mb-2 block">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    required
                    type="password"
                    value={creds.password}
                    onChange={(e) => setCreds({ ...creds, password: e.target.value })}
                    placeholder="••••••••"
                    className="bg-deep-surface border-white/10 text-white input-lux pl-11"
                    data-testid="portal-password"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 text-white/70 cursor-pointer">
                  <input type="checkbox" className="accent-[#D4A574]" /> Remember me
                </label>
                <a href="#" className="text-gold link-gold">Forgot?</a>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-gold w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold tracking-[0.12em] uppercase text-deep hover:bg-gold-light transition-all disabled:opacity-60"
                data-testid="portal-login-btn"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in...</> : <><Moon className="w-4 h-4" /> Sign In</>}
              </button>
            </form>

            <div className="mt-8 pt-6 border-t border-white/5 text-center">
              <p className="text-xs text-white/50">
                New participant? <a href="/contact" className="text-gold link-gold">Get in touch</a> and we'll get you set up.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Logged-in dashboard
  return (
    <div className="relative" data-testid="portal-dashboard">
      <section className="py-16 md:py-20">
        <div className="container-lux">
          {/* Welcome */}
          <div className="flex flex-wrap items-start justify-between gap-6 mb-10">
            <div>
              <div className="text-xs tracking-[0.25em] uppercase text-gold mb-2">Welcome back</div>
              <h1 className="font-serif text-4xl md:text-5xl">Hello, {MOCK_USER.name.split(" ")[0]}.</h1>
              <p className="text-white/60 mt-2">Participant ID: <span className="text-gold">{MOCK_USER.participantId}</span></p>
            </div>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 px-5 py-2.5 text-xs tracking-[0.15em] uppercase text-white/70 hover:border-gold/50 hover:text-gold transition-all"
              data-testid="portal-logout"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-10 border-b border-white/10" data-testid="portal-tabs">
            {[
              { id: "overview", label: "Overview", Icon: User },
              { id: "bookings", label: "My Bookings", Icon: Calendar },
              { id: "messages", label: "Messages", Icon: MessageSquare },
              { id: "plan", label: "My Plan", Icon: FileText },
            ].map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`inline-flex items-center gap-2 px-5 py-3 text-sm border-b-2 transition-all ${
                  tab === id
                    ? "border-gold text-gold"
                    : "border-transparent text-white/60 hover:text-white"
                }`}
                data-testid={`portal-tab-${id}`}
              >
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          {tab === "overview" && (
            <div className="grid lg:grid-cols-3 gap-6" data-testid="portal-overview">
              {/* Plan summary */}
              <div className="lux-card rounded-3xl p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-gold" />
                  </div>
                  <h3 className="font-serif text-xl">Plan Balance</h3>
                </div>
                <div className="font-serif text-4xl text-gold mb-1">{MOCK_USER.planBalance}</div>
                <div className="text-xs text-white/50">Next review: {MOCK_USER.nextReview}</div>
              </div>

              {/* Next session */}
              <div className="lux-card rounded-3xl p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-gold" />
                  </div>
                  <h3 className="font-serif text-xl">Next Session</h3>
                </div>
                <div className="font-serif text-2xl mb-1">{MOCK_BOOKINGS[0].service}</div>
                <div className="text-sm text-white/70">{MOCK_BOOKINGS[0].date} · {MOCK_BOOKINGS[0].time}</div>
                <div className="text-xs text-gold mt-2">with {MOCK_BOOKINGS[0].worker}</div>
              </div>

              {/* Messages count */}
              <div className="lux-card rounded-3xl p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-gold" />
                  </div>
                  <h3 className="font-serif text-xl">Unread Messages</h3>
                </div>
                <div className="font-serif text-4xl text-gold mb-1">{MOCK_MESSAGES.filter(m => m.unread).length}</div>
                <div className="text-xs text-white/50">From your care team</div>
              </div>

              {/* Details */}
              <div className="lux-card rounded-3xl p-8 lg:col-span-3">
                <h3 className="font-serif text-xl mb-5">Participant Details</h3>
                <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
                  {[
                    ["Full Name", MOCK_USER.name],
                    ["Email", MOCK_USER.email],
                    ["NDIS Number", MOCK_USER.ndisNumber],
                    ["Participant ID", MOCK_USER.participantId],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <div className="text-xs uppercase tracking-[0.2em] text-white/50 mb-1">{k}</div>
                      <div className="text-white">{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === "bookings" && (
            <div className="lux-card rounded-3xl overflow-hidden" data-testid="portal-bookings">
              <div className="p-6 md:p-8 border-b border-white/5 flex items-center justify-between">
                <h3 className="font-serif text-2xl">Your Bookings</h3>
                <a href="/book" className="text-xs tracking-[0.15em] uppercase text-gold link-gold">+ Book New</a>
              </div>
              <div className="divide-y divide-white/5">
                {MOCK_BOOKINGS.map((b) => (
                  <div key={b.id} className="grid md:grid-cols-6 gap-4 items-center p-6 md:p-8 hover:bg-white/2 transition-colors">
                    <div className="md:col-span-2">
                      <div className="font-serif text-lg text-white">{b.service}</div>
                      <div className="text-xs text-white/50 mt-1">{b.id}</div>
                    </div>
                    <div className="text-sm text-white/70">{b.worker}</div>
                    <div className="text-sm text-white/70">{b.date}</div>
                    <div className="text-sm text-white/70">{b.time}</div>
                    <div>
                      <span
                        className={`text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-full border ${
                          b.status === "Upcoming"
                            ? "border-gold/40 text-gold bg-gold/5"
                            : b.status === "Pending"
                            ? "border-yellow-500/30 text-yellow-400 bg-yellow-500/5"
                            : "border-white/15 text-white/50"
                        }`}
                      >
                        {b.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "messages" && (
            <div className="lux-card rounded-3xl overflow-hidden" data-testid="portal-messages">
              <div className="p-6 md:p-8 border-b border-white/5">
                <h3 className="font-serif text-2xl">Messages from Your Team</h3>
              </div>
              <div className="divide-y divide-white/5">
                {MOCK_MESSAGES.map((m) => (
                  <div key={m.id} className="flex items-start gap-5 p-6 md:p-8 hover:bg-white/2 transition-colors cursor-pointer">
                    <div className="w-11 h-11 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center text-gold font-serif flex-shrink-0">
                      {m.from.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-medium text-white">{m.from}</span>
                        {m.unread && <span className="w-2 h-2 rounded-full bg-gold" />}
                      </div>
                      <p className="text-sm text-white/70">{m.preview}</p>
                    </div>
                    <div className="text-xs text-white/40 whitespace-nowrap">{m.time}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "plan" && (
            <div className="grid md:grid-cols-2 gap-6" data-testid="portal-plan">
              <div className="lux-card rounded-3xl p-8">
                <h3 className="font-serif text-2xl mb-5">Plan Summary</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between border-b border-white/5 pb-3">
                    <span className="text-white/60">NDIS Number</span>
                    <span className="text-white">{MOCK_USER.ndisNumber}</span>
                  </div>
                  <div className="flex justify-between border-b border-white/5 pb-3">
                    <span className="text-white/60">Current Balance</span>
                    <span className="text-gold font-serif text-lg">{MOCK_USER.planBalance}</span>
                  </div>
                  <div className="flex justify-between border-b border-white/5 pb-3">
                    <span className="text-white/60">Next Review</span>
                    <span className="text-white">{MOCK_USER.nextReview}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Plan Manager</span>
                    <span className="text-white">Moon and Back Support</span>
                  </div>
                </div>
              </div>
              <div className="lux-card rounded-3xl p-8">
                <h3 className="font-serif text-2xl mb-5">Support Budget</h3>
                <div className="space-y-4">
                  {[
                    { name: "Core Supports", used: 45, total: 18000 },
                    { name: "Capacity Building", used: 62, total: 4500 },
                    { name: "Capital Supports", used: 12, total: 2080 },
                  ].map((cat) => (
                    <div key={cat.name}>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-white/80">{cat.name}</span>
                        <span className="text-gold">{cat.used}% used</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-gold-dark to-gold rounded-full transition-all duration-700"
                          style={{ width: `${cat.used}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PortalPage;
